import React from "react";

const DEBT_STATUS = {
  Pending: 0,
  Ongoing: 1,
  Paused: 2,
  Completed: 3,
  Closed: 4,
};

/** Lowercases and trims a status string for theme lookup. */
function normalizeStatus(status) {
  if (status == null || status === "") return "";
  return String(status)
    .trim()
    .toLowerCase();
}

const API_DEBT_STATUS_TO_SLUG = {
  [DEBT_STATUS.Pending]: "pending",
  [DEBT_STATUS.Ongoing]: "ongoing",
  [DEBT_STATUS.Paused]: "paused",
  [DEBT_STATUS.Completed]: "completed",
  [DEBT_STATUS.Closed]: "closed",
};

/**
 * Maps API debt status (numeric code or string) to a stable slug for styling.
 * @param {string|number|null|undefined} status - Raw status from the API or form.
 * @returns {string} Normalized slug (e.g. pending, ongoing) or stringified fallback.
 */
export function apiDebtStatusToSlug(status) {
  if (status == null || status === "") return "";
  if (typeof status === "number" && Number.isFinite(status)) {
    return API_DEBT_STATUS_TO_SLUG[status] ?? String(status);
  }
  const s = String(status)
    .trim()
    .toLowerCase();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return API_DEBT_STATUS_TO_SLUG[n] ?? s;
  }
  return normalizeStatus(status);
}

/** Shared Bootstrap badge classes for debt status pills. */
export const DEBT_STATUS_BADGE_BASE_CLASS =
  "badge badge-pill px-3 py-2 text-uppercase d-inline-flex align-items-center border";

const STATUS_BADGE_THEME = {
  pending: {
    label: "Pending",
    color: "#B45309",
    backgroundColor: "#FEF3C7",
  },
  ongoing: {
    label: "Ongoing",
    color: "#1D4ED8",
    backgroundColor: "#DBEAFE",
  },
  paused: {
    label: "Paused",
    color: "#C2410C",
    backgroundColor: "#FFEDD5",
  },
  completed: {
    label: "Completed",
    color: "#15803D",
    backgroundColor: "#DCFCE7",
  },
  closed: {
    label: "Closed",
    color: "#6B7280",
    backgroundColor: "#F3F4F6",
  },
};

const STATUS_BADGE_FALLBACK = {
  color: "#6B7280",
  backgroundColor: "#F3F4F6",
};

/**
 * Renders a coloured pill for a franchise debt status.
 * @param {{ status?: string|number|null|undefined }} props - Component props.
 * @returns {JSX.Element} Styled status label.
 */
export function DebtStatusBadge({ status }) {
  const slug = apiDebtStatusToSlug(status);
  const s = normalizeStatus(slug);
  const theme = STATUS_BADGE_THEME[s] || {
    label: slug || status || "—",
    ...STATUS_BADGE_FALLBACK,
  };
  const { label, color, backgroundColor } = theme;
  return (
    <span
      className={DEBT_STATUS_BADGE_BASE_CLASS}
      style={{
        fontSize: "0.7rem",
        letterSpacing: "0.04em",
        fontWeight: 600,
        color,
        backgroundColor,
        borderColor: color,
        borderWidth: 1,
        borderStyle: "solid",
      }}
    >
      {label}
    </span>
  );
}
